import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShopHeader } from "@/components/ShopHeader";
import { ShopFooter } from "@/components/ShopFooter";
import { StoreNav } from "@/components/StoreNav";
import { useLang } from "@/context/LanguageContext";
import { CaretRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAN = "/legacy/promo/servitech-vans.jpg";

export default function ContactPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", name: "", subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/shop/contact`, form);
      toast.success(t.contactSent);
      setForm({ email: "", name: "", subject: "", body: "" });
    } catch (err) {
      console.error("contact submit failed", err);
      toast.error(t.contactErr);
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary";
  const Label = ({ children }) => (
    <label className="mb-1.5 block text-sm font-bold text-[#2e3a47]">{children} <span className="text-primary">*</span></label>
  );

  return (
    <div className="min-h-screen bg-white">
      <ShopHeader />
      <div className="mx-auto max-w-[1280px] px-4 py-4"><StoreNav active="contact" /></div>

      <div className="mx-auto max-w-[1280px] px-4 pb-8">
        <div className="mb-8 flex items-center gap-1 text-sm text-muted-foreground">
          <button onClick={() => navigate("/")} className="hover:text-primary">{t.home}</button>
          <CaretRight size={12} weight="bold" />
          <span className="text-foreground">{t.contactHeroTitle}</span>
        </div>

        <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
          {/* Left: van + contact info */}
          <div>
            <img src={VAN} alt="Servitech Xpert" className="mb-10 w-full max-w-[560px] object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <h2 className="mb-5 font-display text-3xl font-black tracking-tight text-[#2e3a47]">{t.contactInfoTitle}</h2>
            <div className="space-y-3 text-[15px] text-foreground/90">
              <p>C.P. 2, Saint-Constant, (Québec) J5A 2G1</p>
              <p><span className="font-bold text-[#2e3a47]">{t.ciEmail.toUpperCase()} :</span> <a href="mailto:info@servitechxpert.com" className="hover:text-primary">info@servitechxpert.com</a></p>
              <p><span className="font-bold text-[#2e3a47]">{t.ciPhone.toUpperCase()} :</span> (514) 475-8974</p>
              <p><span className="font-bold text-[#2e3a47]">{t.ciTollFree.toUpperCase()} :</span> 1-855-200-8974</p>
              <p><span className="font-bold text-[#2e3a47]">{t.ciFax.toUpperCase()} :</span> (450) 638-8974</p>
            </div>
          </div>

          {/* Right: form */}
          <div>
            <h2 className="mb-3 font-display text-3xl font-black tracking-tight text-[#2e3a47]">{t.contactFormTitle}</h2>
            <p className="mb-6 text-[15px] text-foreground/70">{t.contactFormIntro}</p>
            <form onSubmit={submit} data-testid="contact-form">
              <div className="mb-4"><Label>{t.ciEmail}</Label>
                <input type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email" /></div>
              <div className="mb-4"><Label>{t.fldName}</Label>
                <input type="text" required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name" /></div>
              <div className="mb-4"><Label>{t.fldSubject}</Label>
                <input type="text" required value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject" /></div>
              <div className="mb-4"><Label>{t.fldMessage}</Label>
                <textarea rows={6} required value={form.body} onChange={set("body")} className={inputCls} data-testid="contact-body" /></div>
              <div className="bg-slate-100 p-4">
                <button type="submit" disabled={sending} className="rounded bg-primary px-6 py-2.5 text-sm font-bold uppercase tracking-wide text-white transition hover:brightness-95 disabled:opacity-60" data-testid="contact-submit">
                  {sending ? "…" : t.fldSend}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ShopFooter />
    </div>
  );
}
